// notes-walk — recursive directory walker for the Notes section.
//
// Extracted from useNotes so the on-startup cache warmer can also use
// it (warming the walked-list localStorage entry per root means the
// notes file list paints instantly on any list the user might open,
// not just the ones they've previously visited).
//
// Walks the server-side filesystem one notes.listDir at a time and
// flattens the tree to a sortable list. Capped to keep huge libraries
// from blowing the per-request budget on first load.

import { api, type NotesEntry } from "./rpc";

export interface FlatNote {
  /** Path relative to the root (e.g. "ideas/2026.md"). */
  path: string;
  /** Display title — filename sans extension, "/" replaced with " · " for nesting hint. */
  title: string;
  /** Last modified time as ms epoch. */
  mtimeMs: number;
  /** File extension (markdown / py / json etc). For UI hint only. */
  ext: string;
}

const MAX_DEPTH = 4;
const MAX_FILES = 500;

export async function walkRoot(rootId: string): Promise<FlatNote[]> {
  const out: FlatNote[] = [];
  async function visit(path: string, depth: number) {
    if (out.length >= MAX_FILES || depth > MAX_DEPTH) return;
    const entries = await api.notesListDir(rootId, path).catch(() => [] as NotesEntry[]);
    for (const e of entries) {
      if (out.length >= MAX_FILES) return;
      if (e.name.startsWith(".") || e.name === "_templates" || e.name === "_assets") continue;
      const next = path ? `${path}/${e.name}` : e.name;
      if (e.type === "dir") {
        await visit(next, depth + 1);
      } else {
        const dot = e.name.lastIndexOf(".");
        const ext = dot >= 0 ? e.name.slice(dot + 1).toLowerCase() : "";
        const base = dot >= 0 ? e.name.slice(0, dot) : e.name;
        const folder = next.includes("/") ? next.slice(0, next.lastIndexOf("/")) : "";
        const title = folder ? `${folder.replace(/\//g, " · ")} · ${base}` : base;
        out.push({
          path: next,
          title,
          mtimeMs: typeof e.mtimeMs === "number" ? e.mtimeMs : 0,
          ext,
        });
      }
    }
  }
  await visit("", 0);
  return out;
}

// localStorage cache of the walked FlatNote[] per root. useNotes reads
// this on mount to paint the file list before walkRoot finishes; the
// startup cache warmer writes to it for every root so unvisited roots
// also paint instantly.
const WALK_CACHE_KEY = "hub.notes.walkCache.v1";
type WalkCacheBlob = Record<string, FlatNote[]>;

export function readWalkCache(): WalkCacheBlob {
  try {
    const raw = localStorage.getItem(WALK_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}

export function writeWalkCache(rootId: string, list: FlatNote[]) {
  try {
    const cur = readWalkCache();
    cur[rootId] = list;
    localStorage.setItem(WALK_CACHE_KEY, JSON.stringify(cur));
  } catch { /* quota / private-mode — swallow */ }
}

// Sort policy used by the notes file list UI — newest first, then
// alphabetical. Exported so the warmer applies the same sort before
// writing to localStorage, matching what the UI would have done.
export function sortFlatNotes(list: FlatNote[]): FlatNote[] {
  return list.slice().sort((a, b) => (b.mtimeMs - a.mtimeMs) || a.title.localeCompare(b.title));
}
